const MAX_TIMEOUT_DELAY = 2147483647;

class SeasonalNoticeComponent extends HTMLElement {
  connectedCallback() {
    this.startDate = this.dataset.start ? new Date(this.dataset.start) : null;
    this.endDate = this.dataset.end ? new Date(this.dataset.end) : null;
    this.header = document.querySelector('.cc-header');
    this.isSticky = this.classList.contains('seasonal-notice--sticky');
    this.overlayOpen = this.isOverlayOpen();

    this.update();

    if (this.header && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.syncHeaderOffset());
      this.resizeObserver.observe(this);
    }

    if (this.header && !this.isSticky) {
      this.scrollHandler = () => this.syncHeaderOffset();
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    this.resizeHandler = () => this.syncHeaderOffset();
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    this.overlayObserver = new MutationObserver(() => {
      const isOpen = this.isOverlayOpen();
      if (isOpen === this.overlayOpen) return;
      this.overlayOpen = isOpen;
      this.classList.toggle('seasonal-notice--overlay-open', isOpen);
      this.syncHeaderOffset();
    });
    this.overlayObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'open'],
      subtree: true,
    });
  }

  isOverlayOpen() {
    return (
      document.body.classList.contains('fixed') ||
      !!document.querySelector('.main-menu__disclosure.is-open')
    );
  }

  disconnectedCallback() {
    clearTimeout(this.timeoutId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.overlayObserver) this.overlayObserver.disconnect();
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.header) this.header.style.removeProperty('transform');
  }

  update = () => {
    if (window.Shopify?.designMode) {
      this.hidden = false;
      this.syncHeaderOffset();
      return;
    }

    const now = new Date();
    const isActive = (!this.startDate || now >= this.startDate) && (!this.endDate || now <= this.endDate);

    this.hidden = !isActive;
    this.scheduleNextUpdate(now);
    this.syncHeaderOffset();
  };

  syncHeaderOffset() {
    if (!this.header) return;

    const headerIsFixed = getComputedStyle(this.header).position === 'fixed';
    const needsPush = this.isSticky || headerIsFixed;

    if (!needsPush) {
      this.header.style.removeProperty('transform');
      return;
    }

    if (this.hidden || this.overlayOpen) {
      this.header.style.removeProperty('transform');
      return;
    }

    const height = this.getBoundingClientRect().height;
    const offset = this.isSticky ? height : Math.max(0, height - window.scrollY);

    this.header.style.setProperty('transform', `translateY(${offset}px)`, 'important');
  }

  scheduleNextUpdate(now) {
    clearTimeout(this.timeoutId);

    let nextChange = null;
    if (this.startDate && now < this.startDate) {
      nextChange = this.startDate;
    } else if (this.endDate && now <= this.endDate) {
      nextChange = new Date(this.endDate.getTime() + 1000);
    }

    if (!nextChange) return;

    const delay = Math.min(nextChange.getTime() - now.getTime(), MAX_TIMEOUT_DELAY);
    this.timeoutId = setTimeout(this.update, Math.max(delay, 0));
  }
}

if (!customElements.get('seasonal-notice-component')) {
  customElements.define('seasonal-notice-component', SeasonalNoticeComponent);
}
